/**
 * Tick list of a service's catalog work steps (#3347).
 *
 * A service type may list every step a maintenance can include; staff tick
 * the ones to do when the work order or quote is created, instead of deleting
 * lines afterwards. Required steps only show, with a disabled tick; optional
 * ones toggle. The create wizard and the add-service dialog share this.
 */

import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ServiceLaborItem } from '@/hooks/useServices';
import { formatDuration } from '@/utils/duration';

export interface LaborStepOption {
  id: string;
  description: string;
  estimatedHours: number;
  isOptional: boolean;
  selectedByDefault: boolean;
}

export function toLaborStepOption(item: ServiceLaborItem): LaborStepOption {
  return {
    id: item.id,
    description: item.description,
    // The API sends the numeric column as a string ("0.50"); summed as is,
    // two steps concatenate into NaN (#3432).
    estimatedHours: Number(item.estimatedHours),
    isOptional: item.isOptional,
    selectedByDefault: item.selectedByDefault,
  };
}

/** The optional steps that start ticked. */
export function defaultLaborSelection(items: readonly LaborStepOption[]): string[] {
  return items.filter((item) => item.isOptional && item.selectedByDefault).map((item) => item.id);
}

/** Every required step plus the ticked optional ones: what gets copied. */
export function chosenLaborSteps(
  items: readonly LaborStepOption[],
  selected: readonly string[],
): LaborStepOption[] {
  return items.filter((item) => !item.isOptional || selected.includes(item.id));
}

/**
 * Hours of the chosen steps. The server derives a service's planned hours from
 * its copied steps whenever it has any, so the wizard shows the same sum.
 */
export function laborSelectionHours(
  items: readonly LaborStepOption[],
  selected: readonly string[],
): number {
  const total = chosenLaborSteps(items, selected).reduce((sum, item) => sum + item.estimatedHours, 0);
  return Math.round(total * 100) / 100;
}

interface LaborStepChecklistProps {
  items: readonly LaborStepOption[];
  /** Ids of the ticked optional steps. */
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}

export function LaborStepChecklist({ items, selected, onChange, disabled = false }: LaborStepChecklistProps) {
  const { t } = useTranslation('workorders');
  if (items.length === 0) return null;

  const toggle = (id: string, checked: boolean) => {
    onChange(checked ? [...selected.filter((s) => s !== id), id] : selected.filter((s) => s !== id));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }} data-testid="labor-step-checklist">
      <Typography variant="caption" color="text.secondary">
        {t('create.laborStepsHint')}
      </Typography>
      {items.map((item) => {
        const checked = !item.isOptional || selected.includes(item.id);
        return (
          <FormControlLabel
            key={item.id}
            sx={{ mr: 0 }}
            control={(
              <Checkbox
                size="small"
                checked={checked}
                disabled={disabled || !item.isOptional}
                onChange={(event) => toggle(item.id, event.target.checked)}
              />
            )}
            label={(
              <Typography variant="body2" component="span">
                {item.description}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {formatDuration(item.estimatedHours)}
                  {item.isOptional ? '' : ` · ${t('create.laborStepRequired')}`}
                </Typography>
              </Typography>
            )}
          />
        );
      })}
    </Box>
  );
}
